import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Grid2x2 } from "lucide-react";
import api from "@/lib/api";

/**
 * LifePatternsClinicalCard — 2026-XX (Laura)
 *
 * Vista clinica completa dell'analisi per coppia (attività × contesto sociale):
 *   - Scatter Volontarietà (X) × Piacevolezza (Y), 4 quadranti delimitati dalle mediane utente
 *   - 4 card colorate (Scelte vincenti, False promesse, Da rivalutare, Da evitare)
 *   - Tabella completa con frequenza, vol, piac, carico, quadrante
 *
 * MOSTRATO SOLO IN MODALITÀ CLINICO (incluso nel <details> clinical-deep-dive).
 * Le narrative discorsive di queste stesse analisi sono già visibili agli utenti
 * dentro "Cose che abbiamo scoperto su di te".
 */

const QUADRANT_STYLES = {
  winning: {
    label: "Scelte vincenti",
    bg: "bg-emerald-50/60", border: "border-emerald-300",
    text: "text-emerald-900", dot: "bg-emerald-500",
  },
  false_promise: {
    label: "False promesse",
    bg: "bg-amber-50/60", border: "border-amber-300",
    text: "text-amber-900", dot: "bg-amber-500",
  },
  to_revalue: {
    label: "Da rivalutare",
    bg: "bg-sky-50/60", border: "border-sky-300",
    text: "text-sky-900", dot: "bg-sky-500",
  },
  to_avoid: {
    label: "Da evitare / rivedere",
    bg: "bg-stone-50/60", border: "border-stone-300",
    text: "text-stone-700", dot: "bg-stone-400",
  },
  neutral_zone: {
    label: "Zona neutra",
    bg: "bg-slate-50/60", border: "border-slate-200",
    text: "text-slate-700", dot: "bg-slate-300",
  },
};

const ScatterPlot = ({ pairs, thresholds }) => {
  // Coordinate normalizzate 0-10 in entrambi gli assi.
  // SVG 320x260 (con margini per assi).
  const W = 320, H = 260, PAD_L = 38, PAD_B = 28, PAD_T = 8, PAD_R = 8;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xScale = (v) => PAD_L + (v / 10) * innerW;
  const yScale = (v) => PAD_T + (1 - v / 10) * innerH;

  const volMed = thresholds?.voluntariness_median ?? 5;
  const pleasMed = thresholds?.pleasantness_median ?? 5;

  // Raggio in base a sqrt(n) per non far esplodere i pallini.
  const rOf = (n) => Math.max(4, Math.min(12, Math.sqrt(n) * 1.8));

  const colorOf = (q) =>
    ({
      winning: "#10b981",
      false_promise: "#f59e0b",
      to_revalue: "#0ea5e9",
      to_avoid: "#a8a29e",
    }[q] || "#94a3b8");

  return (
    <div className="w-full overflow-x-auto" data-testid="life-patterns-scatter">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ maxWidth: "420px", display: "block", margin: "0 auto" }}
        role="img"
        aria-label="Scatter Volontarietà × Piacevolezza per coppia attività+contesto sociale"
      >
        {/* Quadranti colorati di sfondo */}
        <rect x={PAD_L} y={PAD_T} width={xScale(volMed) - PAD_L} height={yScale(pleasMed) - PAD_T}
              fill="#e0f2fe" fillOpacity="0.5" />
        <rect x={xScale(volMed)} y={PAD_T} width={W - PAD_R - xScale(volMed)} height={yScale(pleasMed) - PAD_T}
              fill="#d1fae5" fillOpacity="0.5" />
        <rect x={PAD_L} y={yScale(pleasMed)} width={xScale(volMed) - PAD_L} height={H - PAD_B - yScale(pleasMed)}
              fill="#f5f5f4" fillOpacity="0.6" />
        <rect x={xScale(volMed)} y={yScale(pleasMed)} width={W - PAD_R - xScale(volMed)} height={H - PAD_B - yScale(pleasMed)}
              fill="#fef3c7" fillOpacity="0.5" />

        {/* Linee mediana */}
        <line x1={xScale(volMed)} y1={PAD_T} x2={xScale(volMed)} y2={H - PAD_B}
              stroke="#78716c" strokeDasharray="3 3" strokeWidth="1" />
        <line x1={PAD_L} y1={yScale(pleasMed)} x2={W - PAD_R} y2={yScale(pleasMed)}
              stroke="#78716c" strokeDasharray="3 3" strokeWidth="1" />

        {/* Assi */}
        <line x1={PAD_L} y1={H - PAD_B} x2={W - PAD_R} y2={H - PAD_B} stroke="#44403c" strokeWidth="1" />
        <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={H - PAD_B} stroke="#44403c" strokeWidth="1" />

        {/* Etichette quadranti */}
        <text x={PAD_L + 4} y={PAD_T + 12} fontSize="9" fill="#0369a1" fontWeight="600">
          Da rivalutare
        </text>
        <text x={W - PAD_R - 4} y={PAD_T + 12} fontSize="9" fill="#047857" fontWeight="600" textAnchor="end">
          Scelte vincenti
        </text>
        <text x={PAD_L + 4} y={H - PAD_B - 6} fontSize="9" fill="#57534e" fontWeight="600">
          Da evitare
        </text>
        <text x={W - PAD_R - 4} y={H - PAD_B - 6} fontSize="9" fill="#b45309" fontWeight="600" textAnchor="end">
          False promesse
        </text>

        {/* Tick assi */}
        <text x={PAD_L} y={H - 8} fontSize="9" fill="#57534e" textAnchor="start">0</text>
        <text x={W - PAD_R} y={H - 8} fontSize="9" fill="#57534e" textAnchor="end">10</text>
        <text x={PAD_L - 4} y={PAD_T + 4} fontSize="9" fill="#57534e" textAnchor="end">10</text>
        <text x={PAD_L - 4} y={H - PAD_B + 2} fontSize="9" fill="#57534e" textAnchor="end">0</text>

        {/* Label assi */}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 2} fontSize="10" fill="#292524" textAnchor="middle" fontWeight="600">
          Volontarietà →
        </text>
        <text
          x={-(PAD_T + (H - PAD_B - PAD_T) / 2)}
          y={11}
          fontSize="10"
          fill="#292524"
          textAnchor="middle"
          fontWeight="600"
          transform="rotate(-90)"
        >
          Piacevolezza →
        </text>

        {/* Pallini */}
        {pairs.map((p, i) => (
          <g key={`${p.activity}-${p.social_context}-${i}`}>
            <circle
              cx={xScale(p.avg_voluntariness)}
              cy={yScale(p.avg_pleasantness)}
              r={rOf(p.n)}
              fill={colorOf(p.quadrant)}
              fillOpacity="0.75"
              stroke="white"
              strokeWidth="1.5"
            >
              <title>
                {p.label} — n={p.n}, Vol={p.avg_voluntariness.toFixed(1)}, Piac={p.avg_pleasantness.toFixed(1)}
              </title>
            </circle>
          </g>
        ))}
      </svg>
      <p className="text-[10px] text-stone-500 text-center mt-1">
        Mediane utente: Vol = {volMed.toFixed(1)} · Piac = {pleasMed.toFixed(1)} (split dei quadranti).
        Dimensione pallino ∝ √N osservazioni.
      </p>
    </div>
  );
};

const QuadrantCard = ({ qKey, q }) => {
  const style = QUADRANT_STYLES[qKey] || QUADRANT_STYLES.to_avoid;
  return (
    <Card
      className={`border ${style.border} ${style.bg}`}
      data-testid={`life-quadrant-${qKey}`}
    >
      <CardContent className="p-2.5 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <span className={`inline-block w-2 h-2 rounded-full ${style.dot}`} />
          <h5 className={`text-[11px] font-semibold ${style.text}`}>
            {style.label} · {q.items.length}
          </h5>
        </div>
        <p className="text-[10px] text-stone-600 leading-tight italic">{q.subtitle}</p>
        {q.items.length === 0 ? (
          <p className="text-[10px] text-stone-400">Nessuna coppia in questo quadrante.</p>
        ) : (
          <ul className="space-y-0.5">
            {q.items.slice(0, 6).map((p) => (
              <li key={`${p.activity}-${p.social_context}`} className="text-[10.5px] text-stone-700 leading-tight">
                <span className="font-medium">{p.label}</span>
                <span className="text-stone-500 tabular-nums">
                  {" "}· n={p.n} · Vol {p.avg_voluntariness.toFixed(1)} · Piac {p.avg_pleasantness.toFixed(1)}
                </span>
              </li>
            ))}
            {q.items.length > 6 && (
              <li className="text-[10px] text-stone-500 italic">
                ...e altre {q.items.length - 6} coppie (vedi tabella sotto).
              </li>
            )}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

const FullTable = ({ pairs }) => (
  <div className="overflow-x-auto border rounded-md" data-testid="life-patterns-table">
    <table className="w-full text-[10.5px]">
      <thead className="bg-stone-50 text-stone-700">
        <tr>
          <th className="text-left px-2 py-1.5 font-semibold">Attività + contesto</th>
          <th className="text-right px-2 py-1.5 font-semibold tabular-nums">N</th>
          <th className="text-right px-2 py-1.5 font-semibold tabular-nums">Vol</th>
          <th className="text-right px-2 py-1.5 font-semibold tabular-nums">Piac</th>
          <th className="text-right px-2 py-1.5 font-semibold tabular-nums">Carico</th>
          <th className="text-left px-2 py-1.5 font-semibold">Quadrante</th>
        </tr>
      </thead>
      <tbody>
        {pairs.map((p) => {
          const style = QUADRANT_STYLES[p.quadrant] || QUADRANT_STYLES.to_avoid;
          return (
            <tr key={`${p.activity}-${p.social_context}`} className="border-t hover:bg-stone-50/50">
              <td className="px-2 py-1 text-stone-800">{p.label}</td>
              <td className="px-2 py-1 text-right tabular-nums">{p.n}</td>
              <td className="px-2 py-1 text-right tabular-nums">{p.avg_voluntariness.toFixed(1)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{p.avg_pleasantness.toFixed(1)}</td>
              <td className="px-2 py-1 text-right tabular-nums">
                {p.avg_load_intensity != null ? p.avg_load_intensity.toFixed(1) : "—"}
              </td>
              <td className="px-2 py-1">
                <Badge variant="outline" className={`text-[9.5px] ${style.border} ${style.text}`}>
                  {style.label}
                </Badge>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const LifePatternsClinicalCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/voice-mood/life-patterns?days=365")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const sortedPairs = useMemo(() => {
    if (!data?.pairs) return [];
    return [...data.pairs].sort((a, b) => b.n - a.n);
  }, [data]);

  if (loading) {
    return (
      <Card className="border" data-testid="life-patterns-loading">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.pairs || data.pairs.length === 0) {
    return (
      <Card className="border border-dashed" data-testid="life-patterns-empty">
        <CardHeader className="pb-1.5 px-3 pt-3">
          <CardTitle className="text-xs sm:text-sm flex items-center gap-1.5">
            <Grid2x2 className="w-4 h-4" /> Pattern di vita · Vol × Piac × Sociale
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          <p className="text-[11px] text-stone-500 leading-relaxed">
            Nessuna coppia (attività + contesto sociale) ha ancora ≥ {data?.min_obs || 5} osservazioni.
            Serve un po&apos; più di dataset per emergere il pattern.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border" data-testid="life-patterns-card">
      <CardHeader className="pb-1.5 px-3 pt-3">
        <CardTitle className="text-xs sm:text-sm flex items-center gap-1.5">
          <Grid2x2 className="w-4 h-4" /> Pattern di vita · Vol × Piac × Sociale
        </CardTitle>
        <p className="text-[10px] text-stone-500 leading-tight">
          {data.total_pairs} coppie (attività × contesto sociale) con N ≥ {data.min_obs}. Soglie = mediane utente.
        </p>
      </CardHeader>
      <CardContent className="px-3 pb-3 space-y-3">
        <ScatterPlot pairs={data.pairs} thresholds={data.thresholds} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {["winning", "to_revalue", "false_promise", "to_avoid", "neutral_zone"].map((k) => (
            data.quadrants?.[k] && (
              <QuadrantCard key={k} qKey={k} q={data.quadrants[k]} />
            )
          ))}
        </div>

        <details className="group" data-testid="life-patterns-table-details">
          <summary className="cursor-pointer list-none flex items-center gap-1.5 text-[11px] font-medium text-stone-700 py-1.5 px-2 rounded border border-stone-200 hover:bg-stone-50">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            Tabella completa ({sortedPairs.length} coppie)
          </summary>
          <div className="pt-2">
            <FullTable pairs={sortedPairs} />
          </div>
        </details>

        <p className="text-[10px] text-stone-500 leading-relaxed border-t pt-2">
          <strong>Lettura clinica:</strong> il quadrante &quot;Da rivalutare&quot; è particolarmente
          rilevante — pattern sottovalutati dall&apos;utente che potrebbero meritare maggiore
          spazio. &quot;False promesse&quot; indica scelte automatiche da indagare (ruminazione,
          procrastinazione, abitudini compensatorie). Le narrative di queste analisi sono
          già visibili agli utenti dentro &quot;Cose che abbiamo scoperto su di te&quot;.
        </p>
      </CardContent>
    </Card>
  );
};

export default LifePatternsClinicalCard;
